import { run } from '@ember/runloop';
import EmberService from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type Model from '@ember-data/model';

import { equal } from 'webapp/utils/common';

type SerializedRecord = Record<string, unknown>;

interface QueueItem<T extends Model = Model> {
	model: T;
	props?: SerializedRecord; // backup of the model's newer data while the same model is being saved
	promise?: Promise<T>;
	onSuccess(model?: T): void;
	onError(error: unknown): void;
}

export default class RequestQueueService extends EmberService {
	@tracked queue: QueueItem[] = [];
	@tracked current: Model[] = [];

	get isRequestInProgress(): boolean {
		return this.current.length > 0 || this.queue.length > 0;
	}

	private findDifferences(
		recOneJSON: SerializedRecord,
		recTwoJSON: SerializedRecord | undefined,
	): SerializedRecord {
		const diff: SerializedRecord = {};

		if (recTwoJSON) {
			Object.keys(recTwoJSON).forEach((prop) => {
				const recOneVal = recOneJSON[prop],
					recTwoVal = recTwoJSON[prop];

				if (!equal(recTwoVal, recOneVal)) {
					diff[prop] = recTwoVal;
				}
			});
		}

		return diff;
	}

	private checkForWork(): void {
		const item = this.queue[0];

		if (item) {
			// if the same model is in current, we need to prevent the response
			// overwriting our currently queued data, so we serialize this data in property backup
			if (this.current.includes(item.model) && !item.props) {
				item.props = item.model.serialize() as SerializedRecord;
			} else {
				if (item.model) {
					this.doWork(item);
				} else {
					// could be that model was deleted while in queue
					this.queue = this.queue.filter((i) => i !== item);
				}
			}
		}
	}

	private doWork(item: QueueItem): void {
		const doRequest = true,
			saveCallback = (data?: Model) => {
				run(() => {
					item.onSuccess(data);

					this.current = this.current.filter((i) => i !== item.model);
					this.checkForWork();
				});
			},
			errorCallback = (dsError: unknown) => {
				run(() => {
					item.onError(dsError);

					this.current = this.current.filter((i) => i !== item.model);
					this.checkForWork();
				});
			};

		// put obj out of waiting queue and mark as being currently processed
		this.current = [...this.current, item.model];
		this.queue = this.queue.filter((i) => i !== item);

		// find differences: what property backup has that current model doesn't
		// we update props whenever model buffer changes while model is less flexible
		// as you may not update it while inFlight
		const differences = this.findDifferences(
			item.model.serialize() as SerializedRecord,
			item.props,
		);

		// if we have changes, we merge them into model before doing request
		if (Object.keys(differences).length > 0) {
			item.model.setProperties(differences);
			// } else if (item.props) {
			// 	// if props are set, it means this item waited in queue
			// 	// but since there were no differences, we skip the request
			// 	doRequest = false;
		}

		if (doRequest) {
			item.model.save().then(saveCallback, errorCallback);
		} else {
			saveCallback();
		}
	}

	add<T extends Model>(model: T): Promise<T> {
		const objInQueue = this.queue.find((item) => item.model === model);
		let promise: Promise<T>;

		if (!objInQueue) {
			promise = new Promise<T>((resolve, reject) => {
				this.queue.push({
					model,
					// promise: promise, // promise is still undefined here!
					onSuccess: (savedModel) => resolve(savedModel as T), // the queued model itself
					onError: reject,
				});

				this.checkForWork();
			});

			const queueObj = this.queue.find((item) => item.model === model);
			if (queueObj) {
				queueObj.promise = promise;
			}
		} else {
			// if same model is already in queue, we simply update 'backup' props with the newer data that's changed
			$.extend(objInQueue.props, model.serialize());
			//differences = this.findDifferences(objInQueue.props, model.serialize());
			//$.extend(objInQueue.props, differences);
			promise = objInQueue.promise as Promise<T>; // the same model, so the same T
		}

		return promise;
	}
}

declare module '@ember/service' {
	interface Registry {
		'request-queue': RequestQueueService;
	}
}
